import { estimatesApi } from "@/lib/api/estimates"
import {
  agentsApi,
  type AgentJobStatus,
  type AgentJobType,
} from "@/lib/api/agents"
import type { QEPhoto } from "../quick-estimate/types"

// Full Estimate — submit + agent pipeline.
//
// Differs from QE3:
//   • damage runs WITHOUT quickEstimateMode so the BE does not
//     auto-chain major-operations + cost. The line items it produces
//     already include action (Replace/Repair) + partType, so we don't
//     need a follow-up /major-operations call.
//
// The damage agent returns a jobId that we ping until finishedOn lands.
// The caller (step-processing) then re-fetches the estimate and renders
// the damage editor.
//
// `/major-operations`, `/cost` and `/sections-orchestrator` are NOT
// called here — sections-orchestrator runs later (after the user PUTs
// line-item edits on the Damage step's Next button). When it does run,
// we don't ping the BullMQ job; we poll the estimate every 5s and wait
// until every line item has at least one operation, since that's the
// data the Operations step actually renders. `/major-operations` and
// `/cost` are not used in this flow at all.

export interface FullEstimateSubmitArgs {
  teamId: number | string
  vin: string
  vehicle: {
    year: string | null
    make: string | null
    model: string | null
    trim: string | null
  } | null
  photos: QEPhoto[]
  notes: string
  customer: {
    name: string
    phone: string
  }
}

export interface FullEstimateSubmitResult {
  estimateId: number
  photosAttached: number
}

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

async function pollJob(
  jobId: string,
  type?: AgentJobType,
): Promise<AgentJobStatus> {
  const startedAt = Date.now()
  while (true) {
    const status = await agentsApi.pingJob(jobId, type)
    if (status.failedReason) {
      throw new Error(status.failedReason)
    }
    if (status.finishedOn || status.status === "completed") {
      return status
    }
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      throw new Error("Timed out waiting for agent")
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
  }
}

export async function submitFullEstimateCreate(
  args: FullEstimateSubmitArgs,
): Promise<FullEstimateSubmitResult> {
  // 1. Create the estimate shell.
  const estimate = await estimatesApi.startFull(args.teamId, {
    customer: {
      name: args.customer.name.trim() || "—",
      ...(args.customer.phone.trim() && { phone: args.customer.phone.trim() }),
    },
    vehicle: {
      vin: args.vin,
      ...(args.vehicle?.year && { year: args.vehicle.year }),
      ...(args.vehicle?.make && { make: args.vehicle.make }),
      ...(args.vehicle?.model && { model: args.vehicle.model }),
      ...(args.vehicle?.trim && { trim: args.vehicle.trim }),
    },
  })

  // 2. Attach images. BE requires a non-empty `imageUrl` and a
  // non-empty `description` per row (DTO: @IsString() @IsNotEmpty()).
  // Photos with no uploadedUrl are dropped — the UI hard-blocks the
  // no-photo case for full-estimate but defend at the boundary.
  const submittable = args.photos.filter((p) => p.uploadedUrl.length > 0)
  let photosAttached = 0
  if (submittable.length > 0) {
    const images = submittable.map((p, idx) => {
      const perPhoto = p.description?.trim()
      // Compose a non-empty description: per-photo note → global notes
      // (first row only) → fall back to the side label so the BE always
      // sees a valid string.
      const fallback = `${p.detectedSide.toLowerCase().replace(/_/g, " ")} view`
      const description =
        perPhoto && perPhoto.length > 0
          ? perPhoto
          : idx === 0 && args.notes.trim().length > 0
            ? args.notes.trim()
            : fallback
      return {
        imageUrl: p.uploadedUrl,
        side: p.detectedSide,
        qualityScore: clampQuality(p.qualityScore),
        qualified: p.qualified,
        description,
      }
    })
    // Don't swallow — if addImages fails the estimate exists but is
    // empty, and the next step's agent run will operate on no images.
    // Surfacing the error lets the processing screen show "couldn't
    // generate" instead of hanging on an empty estimate.
    await estimatesApi.addImages(estimate.id, { images })
    photosAttached = images.length
  }

  return { estimateId: Number(estimate.id), photosAttached }
}

function clampQuality(n: number | null | undefined): number {
  // BE DTO requires int 0..100. Photo evaluation may return floats or
  // rare out-of-range values; clamp + round to keep the call valid.
  if (n == null || Number.isNaN(n)) return 0
  return Math.max(0, Math.min(100, Math.round(n)))
}

export async function runDamageAgent(estimateId: number): Promise<void> {
  // Damage with quickEstimateMode=false → BE returns line items
  // (with action + partType) and stops. No major-ops, no cost.
  const damageJob = await estimatesApi.runDamageEstimation(estimateId, {
    quickEstimateMode: false,
  })
  if (damageJob.jobId) {
    await pollJob(damageJob.jobId)
  }
}

const ORCHESTRATOR_POLL_INTERVAL_MS = 5000

export async function runSectionsOrchestratorAndWait(
  estimateId: number,
): Promise<void> {
  await estimatesApi.runSectionsOrchestrator(estimateId)
  // Instead of pinging the BullMQ job, poll the estimate itself and
  // wait until every line item has at least one operation. This is
  // what the Operations step actually renders, so it's the correct
  // readiness gate — and it works identically against the mock (which
  // returns no jobId) and the real BE.
  const startedAt = Date.now()
  while (true) {
    await new Promise((r) => setTimeout(r, ORCHESTRATOR_POLL_INTERVAL_MS))
    const est = await estimatesApi.get(estimateId)
    const items = est.lineItems ?? []
    if (
      items.length > 0 &&
      items.every((li) => (li.operations?.length ?? 0) > 0)
    ) {
      return
    }
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      throw new Error("Timed out waiting for operations")
    }
  }
}
